import { Component, Host, h, Prop, Watch, Element } from '@stencil/core';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';

export interface UiCmsContactPersonBody {
  title?: string;
  name?: string;
  image?: string;
  details?: string;
  tasks?: string;
  streetAddress?: string;
  zipCode?: string;
  place?: string;
  country?: string;
  phoneNumber?: string;
  mobileNumber?: string;
  faxNumber?: string;
  emailAddress?: string;
  emailAddressLabel?: string;
  xingUrl?: string;
  xingLabel?: string;
  linkedInUrl?: string;
  linkedInLabel?: string;
  homePageUrl?: string;
  homePageLabel?: string;
  googleMapsUrl?: string;
  googleMapsLabel?: string;
}

@Component({
  tag: 'ui-cms-contact-person',
  styleUrl: 'ui-cms-contact-person.scss',
  shadow: true,
})
export class UiCmsContactPerson {
  @Element() el: HTMLUiCmsContactPersonElement;

  @Prop({ mutable: true }) body: UiCmsContactPersonBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  bodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  private city: string;

  componentWillLoad() {
    this.bodyChangeHandler(this.utilityBody);
    this.city =
      (this.body.place || this.body.zipCode) && [this.body.zipCode, this.body.place].filter(Boolean).join(' ');
  }

  private linkRenderer(href: string, label: string, iconName?: string) {
    return (
      <sd-link target="_blank" href={href} onClick={() => this.handleTracking(href, label)}>
        {iconName && <sd-icon slot="icon-left" name={`union-investment/system/${iconName}`}></sd-icon>}
        {label}
      </sd-link>
    );
  }

  private removeSpaces(text: string): string {
    return text.split(' ').join('');
  }

  private handleTracking(href: string, label: string): void {
    const isTel = href.includes('tel') || href.includes('fax');

    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName: `Click on ${label}`,
        eventType: 'Action',
      },
      attributes: {
        elementType: 'Link',
        elementLabel: isTel ? this.removeSpaces(label) : label,
        target: isTel ? this.removeSpaces(href) : href,
      },
      category: {
        area: 'Content',
      },
    };

    window.ui?.services?.tracking?.track(trackingEvent);
  }

  render() {
    return (
      <Host>
        <div class="content-wrapper">
          {this.body?.image && (
            <div class="image">
              <figure class="sd-media">
                <ui-cms-image src={this.body.image} alt="" size={640} />
              </figure>
            </div>
          )}
          <address class="description">
            {this.body.title && <p class="sd-meta sd-meta--light sd-meta--size-sm">{this.body.title}</p>}
            {this.body.name && <p class="sd-headline sd-headline--size-lg name">{this.body.name}</p>}
            {(this.body.details || this.body.tasks) && (
              <div class="details">
                {this.body.details && <p class="sd-paragraph">{this.body.details}</p>}
                {this.body.tasks && <p class="sd-paragraph">{this.body.tasks}</p>}
              </div>
            )}
            {(this.body.streetAddress || this.city || this.body.googleMapsUrl) && (
              <div class="address">
                {this.body.streetAddress && <p class="sd-paragraph">{this.body.streetAddress}</p>}
                {this.city && <p class="sd-paragraph">{this.city}</p>}
                {this.body.country && <p class="sd-paragraph">{this.body.country}</p>}
                {this.body.googleMapsUrl &&
                  this.linkRenderer(
                    this.body.googleMapsUrl,
                    this.body.googleMapsLabel ? this.body.googleMapsLabel : this.body.googleMapsUrl,
                    'position'
                  )}
              </div>
            )}
            {(this.body.phoneNumber || this.body.mobileNumber || this.body.faxNumber || this.body.emailAddress) && (
              <div class="contacts">
                {this.body.phoneNumber &&
                  this.linkRenderer(`tel:${this.body.phoneNumber}`, this.body.phoneNumber, 'phone')}
                {this.body.mobileNumber &&
                  this.linkRenderer(`tel:${this.body.mobileNumber}`, this.body.mobileNumber, 'mobilephone')}
                {this.body.faxNumber && this.linkRenderer(`fax:${this.body.faxNumber}`, this.body.faxNumber, 'fax')}
                {this.body.emailAddress &&
                  this.linkRenderer(
                    `mailto:${this.body.emailAddress}`,
                    this.body.emailAddressLabel ? this.body.emailAddressLabel : this.body.emailAddress,
                    'e-mail'
                  )}
              </div>
            )}
            {(this.body.linkedInUrl || this.body.xingUrl) && (
              <div class="social-links">
                {this.body.linkedInUrl &&
                  this.linkRenderer(
                    this.body.linkedInUrl,
                    this.body.linkedInLabel ? this.body.linkedInLabel : 'LinkedIn',
                    'linkedin'
                  )}
                {this.body.xingUrl &&
                  this.linkRenderer(this.body.xingUrl, this.body.xingLabel ? this.body.xingLabel : 'Xing', 'xing')}
              </div>
            )}
            {this.body.homePageUrl &&
              this.linkRenderer(
                this.body.homePageUrl,
                this.body.homePageLabel ? this.body.homePageLabel : this.body.homePageUrl
              )}
          </address>
        </div>
      </Host>
    );
  }
}
